import React, { useState, useRef, useEffect } from 'react';
import { Calendar, Clock, User, Phone, Mail, MapPin, Briefcase, MessageSquare, Image as ImageIcon, Upload, Loader2, X } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useLocation } from 'react-router-dom';
import { translations } from '../translations/translations';
import axios from 'axios';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BookingPage = () => {
  const { language } = useLanguage();
  const location = useLocation();
  const t = translations[language] || translations.pt;
  const fileInputRef = useRef(null);

  // Detect region from URL
  const getRegion = () => {
    if (location.pathname.startsWith('/ie')) return 'ie';
    return 'pt';
  };
  const region = getRegion();

  // Helper function for inline translations
  const getText = (ptText, enText, esText) => {
    if (language === 'es') return esText;
    if (language === 'en') return enText;
    return ptText;
  };

  const [selectedDate, setSelectedDate] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    service_type: '',
    preferred_time: '',
    address: '',
    eircode: '',
    notes: '',
    photos: []
  });
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Build service options based on region
  const getServiceOptions = () => {
    if (region === 'ie') {
      // Ireland - Only cleaning services (House Cleaning focused)
      // Some services marked as "Fully Booked"
      const services = [
        { pt: 'Limpeza Regular', en: 'Regular House Cleaning', es: 'Limpieza Regular', available: true },
        { pt: 'Limpeza Profunda', en: 'Deep Cleaning', es: 'Limpieza Profunda', available: true },
        { pt: 'Limpeza de Escritório', en: 'Office Cleaning', es: 'Limpieza de Oficina', available: true },
        { pt: 'Limpeza Fim de Arrendamento', en: 'End of Tenancy Cleaning', es: 'Limpieza Fin de Alquiler', available: false },
        { pt: 'Limpeza de Estofados', en: 'Upholstery Cleaning', es: 'Limpieza de Tapicería', available: false }
      ];
      return services.map(s => ({
        label: language === 'es' ? s.es : language === 'en' ? s.en : s.pt,
        available: s.available
      }));
    } else {
      // Portugal - All services
      const services = [
        { pt: 'Pintura Externa', en: 'External Painting', es: 'Pintura Externa' },
        { pt: 'Pintura Interna', en: 'Internal Painting', es: 'Pintura Interna' },
        { pt: 'Pequenos Reparos', en: 'Small Repairs', es: 'Pequeñas Reparaciones' },
        { pt: 'Limpeza Profunda', en: 'Deep Cleaning', es: 'Limpieza Profunda' },
        { pt: 'Limpeza Doméstica', en: 'Regular Cleaning', es: 'Limpieza Doméstica' },
        { pt: 'Manutenção Geral', en: 'General Maintenance', es: 'Mantenimiento General' },
        { pt: 'Montagem de Móveis', en: 'Furniture Assembly', es: 'Montaje de Muebles' }
      ];
      return services.map(s => ({
        label: language === 'es' ? s.es : language === 'en' ? s.en : s.pt,
        available: true
      }));
    }
  };

  const serviceOptions = getServiceOptions();
  const [bookedSlots, setBookedSlots] = useState([]);

  // Time slots - all hours shown, but morning slots on weekdays will be marked as unavailable
  const allTimeSlots = ['09:00', '09:30', '10:00', '10:30', '11:00', '11:30', '12:00', '12:30', '13:00', '13:30', '14:00', '14:30', '15:00', '15:30', '16:00', '16:30', '17:00', '17:30', '18:00', '18:30', '19:00', '19:30', '20:00', '20:30', '21:00'];
  
  const timeSlots = region === 'ie' 
    ? allTimeSlots
    : ['08:00', '09:00', '10:00', '11:00', '12:00', '14:00', '15:00', '16:00', '17:00', '18:00'];

  // Helper to format date without timezone issues
  const formatLocalDate = (date) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Fetch booked slots when date changes
  useEffect(() => {
    const fetchBookedSlots = async () => {
      if (region === 'ie' && selectedDate) {
        try {
          const dateStr = formatLocalDate(selectedDate);
          const response = await axios.get(`${API}/availability?date=${dateStr}&region=ie`);
          setBookedSlots(response.data.booked_slots || []);
        } catch (error) {
          console.error('Error fetching availability:', error);
          setBookedSlots([]);
        }
      }
    };
    fetchBookedSlots();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, region]);

  // Check if a time slot is available (for Ireland, morning slots on weekdays are "fully booked")
  const isTimeSlotAvailable = (time, date) => {
    if (region !== 'ie' || !date) return true;
    
    // Check if this slot is booked
    const dateStr = formatLocalDate(date);
    const isBooked = bookedSlots.some(slot => slot.date === dateStr && slot.time === time);
    if (isBooked) return false;
    
    const dayOfWeek = date.getDay(); // 0 = Sunday, 6 = Saturday
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
    
    if (isWeekend) return true; // All slots available on weekends
    
    // On weekdays, morning slots (before 14:30) are "fully booked"
    const hour = parseInt(time.split(':')[0]);
    const minute = parseInt(time.split(':')[1]);
    const timeInMinutes = hour * 60 + minute;
    
    return timeInMinutes >= 14 * 60 + 30; // Available from 14:30
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    if (formData.photos.length + files.length > 5) {
      alert(language === 'pt' ? 'Máximo de 5 fotos permitido' : 'Maximum of 5 photos allowed');
      return;
    }

    setUploadingPhoto(true);
    
    try {
      const uploadPromises = Array.from(files).map(async (file) => {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        
        const response = await axios.post(`${API}/upload`, formDataUpload, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        return `${BACKEND_URL}${response.data.url}`;
      });
      
      const uploadedUrls = await Promise.all(uploadPromises);
      
      setFormData(prev => ({
        ...prev,
        photos: [...prev.photos, ...uploadedUrls]
      }));
    } catch (error) {
      console.error('Error uploading file:', error);
      alert(language === 'pt' ? 'Erro ao enviar imagem. Tente novamente.' : 'Error uploading image. Please try again.');
    } finally {
      setUploadingPhoto(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const removePhoto = (index) => {
    setFormData(prev => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedDate) {
      setSubmitStatus({ 
        type: 'error', 
        message: language === 'pt' ? 'Por favor, selecione uma data' : 'Please select a date' 
      });
      return;
    }

    setShowConfirmModal(true);
  };

  const confirmAndSubmit = async () => {
    setIsSubmitting(true);
    setSubmitStatus({ type: '', message: '' });
    setShowConfirmModal(false);

    try {
      const appointmentData = {
        ...formData,
        preferred_date: formatLocalDate(selectedDate),
        region: region
      };

      await axios.post(`${API}/appointments`, appointmentData);
      
      setSubmitStatus({ 
        type: 'success', 
        message: language === 'pt' 
          ? 'Agendamento enviado com sucesso! Entraremos em contacto em breve para confirmar.' 
          : 'Appointment submitted successfully! We will contact you soon to confirm.'
      });
      
      setFormData({
        name: '',
        phone: '',
        email: '',
        service_type: '',
        preferred_time: '',
        address: '',
        eircode: '',
        notes: '',
        photos: []
      });
      setSelectedDate(null);
    } catch (error) {
      console.error('Error submitting appointment:', error);
      setSubmitStatus({ 
        type: 'error', 
        message: language === 'pt' 
          ? 'Erro ao enviar agendamento. Por favor, tente novamente.' 
          : 'Error submitting appointment. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen" data-testid="booking-page">
      <section className="py-16 md:py-24 bg-gradient-to-b from-primary to-primary-dark text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Calendar size={64} className="mx-auto mb-6" />
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold mb-6">
            {getText('Agendar Serviço', 'Book Service', 'Agendar Servicio')}
          </h1>
          <p className="text-lg md:text-xl font-body max-w-3xl mx-auto mb-6">
            {getText(
              'Orçamento aprovado? Agende aqui a data e hora do serviço',
              'Quote approved? Schedule your service date and time here',
              '¿Presupuesto aprobado? Programe aquí la fecha y hora del servicio'
            )}
          </p>
          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-4 max-w-2xl mx-auto">
            <p className="text-sm font-body">
              {getText(
                '💡 Ainda não pediu orçamento? Use "Pedir Orçamento" no menu primeiro.',
                '💡 Haven\'t requested a quote yet? Use "Get Quote" in the menu first.',
                '💡 ¿Aún no ha pedido presupuesto? Use "Pedir Presupuesto" en el menú primero.'
              )}
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Pricing Reference Section - Only for Ireland */}
          {region === 'ie' && (
            <div className="mb-12 bg-gradient-to-r from-[#1a5c3a]/5 to-[#d4a84b]/5 rounded-2xl p-8 border border-[#1a5c3a]/10">
              <h3 className="text-2xl font-heading font-bold text-gray-900 mb-6 text-center">
                {language === 'pt' ? 'Referência de Preços' : 'Price Reference'}
              </h3>
              
              {/* Main Services */}
              <div className="grid md:grid-cols-3 gap-6 mb-8">
                <div className="bg-white rounded-xl p-5 shadow-sm">
                  <div className="text-2xl mb-2">🏠</div>
                  <h4 className="font-heading font-bold text-gray-900 mb-2">
                    {language === 'pt' ? 'Limpeza Regular' : 'Regular House Cleaning'}
                  </h4>
                  <p className="text-[#1a5c3a] font-bold">
                    {language === 'pt' ? 'Desde' : 'From'} €25/hr <span className="text-gray-500 font-normal text-sm">(1 cleaner)</span>
                  </p>
                  <p className="text-[#1a5c3a] font-bold">
                    {language === 'pt' ? 'Desde' : 'From'} €50/hr <span className="text-gray-500 font-normal text-sm">(2 cleaners)</span>
                  </p>
                </div>
                <div className="bg-white rounded-xl p-5 shadow-sm border-2 border-[#d4a84b]">
                  <div className="text-2xl mb-2">✨</div>
                  <h4 className="font-heading font-bold text-gray-900 mb-2">
                    {language === 'pt' ? 'Limpeza Profunda' : 'Deep Cleaning'}
                  </h4>
                  <p className="text-[#1a5c3a] font-bold">
                    {language === 'pt' ? 'Desde' : 'From'} €30/hr <span className="text-gray-500 font-normal text-sm">(1 cleaner)</span>
                  </p>
                  <span className="inline-block mt-2 px-2 py-1 bg-[#d4a84b] text-white text-xs rounded-full">
                    {language === 'pt' ? 'Mais Popular' : 'Most Popular'}
                  </span>
                </div>
                <div className="bg-white rounded-xl p-5 shadow-sm">
                  <div className="text-2xl mb-2">🚚</div>
                  <h4 className="font-heading font-bold text-gray-900 mb-2">
                    {language === 'pt' ? 'Mudança' : 'Move-In / Move-Out'}
                  </h4>
                  <p className="text-[#d4a84b] font-bold">
                    {language === 'pt' ? 'Orçamento Personalizado' : 'Custom Quote'}
                  </p>
                </div>
              </div>

              {/* Extra Services */}
              <div className="mb-6">
                <h4 className="font-heading font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <span>🧼</span> {language === 'pt' ? 'Serviços Extra' : 'Extra Services'}
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="bg-white rounded-lg p-3 text-center">
                    <p className="text-sm text-gray-600">{language === 'pt' ? 'Forno' : 'Oven'}</p>
                    <p className="font-bold text-[#1a5c3a]">€40+</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 text-center">
                    <p className="text-sm text-gray-600">{language === 'pt' ? 'Frigorífico' : 'Fridge'}</p>
                    <p className="font-bold text-[#1a5c3a]">€25+</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 text-center">
                    <p className="text-sm text-gray-600">{language === 'pt' ? 'Janelas' : 'Windows'}</p>
                    <p className="font-bold text-[#1a5c3a]">€30+</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 text-center">
                    <p className="text-sm text-gray-600">{language === 'pt' ? 'Engomar' : 'Ironing'}</p>
                    <p className="font-bold text-[#1a5c3a]">€20/hr</p>
                  </div>
                </div>
              </div>

              {/* Booking Info */}
              <div className="bg-white rounded-xl p-4 mb-4">
                <div className="flex flex-wrap gap-4 justify-center text-sm">
                  <span className="flex items-center gap-2 text-gray-700">
                    <span className="text-[#1a5c3a]">✓</span> {language === 'pt' ? 'Mínimo: 2 horas' : 'Minimum: 2 hours'}
                  </span>
                  <span className="flex items-center gap-2 text-gray-700">
                    <span className="text-[#1a5c3a]">✓</span> {language === 'pt' ? '1 ou 2 profissionais' : '1 or 2 cleaners'}
                  </span>
                  <span className="flex items-center gap-2 text-gray-700">
                    <span className="text-[#1a5c3a]">✓</span> {language === 'pt' ? 'Serviço profissional' : 'Professional service'}
                  </span>
                </div>
              </div>

              {/* Important Note */}
              <div className="bg-[#d4a84b]/10 rounded-lg p-4 border border-[#d4a84b]/30">
                <p className="text-sm text-gray-700 text-center">
                  <strong>{language === 'pt' ? 'Nota:' : 'Note:'}</strong>{' '}
                  {language === 'pt' 
                    ? 'Os preços apresentados são valores iniciais. O orçamento final será confirmado após análise de fotos ou avaliação no local.'
                    : 'Prices shown are starting prices. Final quote confirmed after reviewing photos or on-site assessment.'}
                </p>
              </div>
            </div>
          )}

          <div className="bg-blue-50 border-l-4 border-primary p-4 mb-8">
            <p className="text-sm font-body text-gray-700">
              <strong>{language === 'pt' ? 'Esta área é para:' : 'This area is for:'}</strong>
              <br />
              {language === 'pt'
                ? '✓ Clientes com orçamento já aprovado | ✓ Agendar data e hora específica | ✓ Confirmação de serviço'
                : '✓ Clients with approved quote | ✓ Schedule specific date and time | ✓ Service confirmation'}
            </p>
          </div>
          <form onSubmit={handleSubmit} data-testid="booking-form" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="name" className="block text-sm font-body font-medium text-gray-700 mb-2">
                  <User size={18} className="inline mr-2" />
                  {language === 'pt' ? 'Nome Completo' : 'Full Name'}
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  data-testid="booking-input-name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full h-12 px-4 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent font-body"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-body font-medium text-gray-700 mb-2">
                  <Phone size={18} className="inline mr-2" />
                  {language === 'pt' ? 'Telefone' : 'Phone'}
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  data-testid="booking-input-phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full h-12 px-4 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent font-body"
                />
              </div>
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-body font-medium text-gray-700 mb-2">
                <Mail size={18} className="inline mr-2" />
                Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                data-testid="booking-input-email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full h-12 px-4 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent font-body"
              />
            </div>

            <div>
              <label htmlFor="service_type" className="block text-sm font-body font-medium text-gray-700 mb-2">
                <Briefcase size={18} className="inline mr-2" />
                {language === 'pt' ? 'Tipo de Serviço' : 'Service Type'}
              </label>
              <select
                id="service_type"
                name="service_type"
                data-testid="booking-select-service"
                value={formData.service_type}
                onChange={handleChange}
                required
                className="w-full h-12 px-4 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent font-body"
              >
                <option value="">{language === 'pt' ? 'Selecione um serviço' : 'Select a service'}</option>
                {serviceOptions.map((service, index) => (
                  <option 
                    key={index} 
                    value={service.available ? service.label : ''} 
                    disabled={!service.available}
                  >
                    {service.label}{!service.available ? ` (${language === 'pt' ? 'Agenda Cheia' : 'Fully Booked'})` : ''}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-body font-medium text-gray-700 mb-2">
                  <Calendar size={18} className="inline mr-2" />
                  {language === 'pt' ? 'Data Preferida' : 'Preferred Date'}
                </label>
                <DatePicker
                  selected={selectedDate}
                  onChange={(date) => setSelectedDate(date)}
                  minDate={new Date()}
                  dateFormat="dd/MM/yyyy"
                  placeholderText={language === 'pt' ? 'Escolha a data' : 'Choose date'}
                  className="w-full h-12 px-4 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent font-body"
                  data-testid="booking-datepicker"
                  required
                />
              </div>

              <div>
                <label htmlFor="preferred_time" className="block text-sm font-body font-medium text-gray-700 mb-2">
                  <Clock size={18} className="inline mr-2" />
                  {language === 'pt' ? 'Hora Preferida' : 'Preferred Time'}
                </label>
                <select
                  id="preferred_time"
                  name="preferred_time"
                  data-testid="booking-select-time"
                  value={formData.preferred_time}
                  onChange={handleChange}
                  required
                  className="w-full h-12 px-4 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent font-body"
                >
                  <option value="">{language === 'pt' ? 'Selecione a hora' : 'Select time'}</option>
                  {timeSlots.map((time, index) => {
                    const available = isTimeSlotAvailable(time, selectedDate);
                    return (
                      <option 
                        key={index} 
                        value={available ? time : ''} 
                        disabled={!available}
                      >
                        {time}{!available ? ` (${language === 'pt' ? 'Agenda Cheia' : 'Fully Booked'})` : ''}
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>

            {/* Preference Notice */}
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800">
                <span className="font-medium">⚠️ {language === 'pt' ? 'Nota:' : 'Note:'}</span>{' '}
                {language === 'pt' 
                  ? 'A data e hora selecionadas são apenas uma preferência. A confirmação final será fornecida após a aprovação do orçamento e confirmação da disponibilidade.'
                  : 'The selected date and time are a preference only. Final confirmation will be provided after your quote is approved and availability is confirmed.'}
              </p>
            </div>

            <div>
              <label htmlFor="address" className="block text-sm font-body font-medium text-gray-700 mb-2">
                <MapPin size={18} className="inline mr-2" />
                {language === 'pt' ? 'Endereço da Residência' : 'Residence Address'}
              </label>
              <input
                type="text"
                id="address"
                name="address"
                data-testid="booking-input-address"
                value={formData.address}
                onChange={handleChange}
                required
                placeholder={language === 'pt' ? 'Ex: Rua Principal, 123, Faro' : 'Ex: Main Street, 123, Dublin'}
                className="w-full h-12 px-4 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent font-body"
              />
              <p className="text-xs text-gray-500 mt-1 font-body">
                {language === 'pt' 
                  ? 'Informe o endereço completo onde o serviço será realizado' 
                  : 'Enter the complete address where the service will be performed'}
              </p>
            </div>

            {/* Eircode field - Ireland only */}
            {region === 'ie' && (
              <div>
                <label htmlFor="eircode" className="block text-sm font-body font-medium text-gray-700 mb-2">
                  <MapPin size={18} className="inline mr-2" />
                  Eircode
                  <span className="text-gray-400 font-normal ml-2">(e.g. D02 X285)</span>
                </label>
                <input
                  type="text"
                  id="eircode"
                  name="eircode"
                  data-testid="booking-input-eircode"
                  value={formData.eircode}
                  onChange={handleChange}
                  placeholder="A65 F4E2"
                  maxLength={8}
                  className="w-full h-12 px-4 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent font-body uppercase"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {"Don't know your Eircode? "}<a href="https://finder.eircode.ie" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Find it here</a>
                </p>
              </div>
            )}

            <div>
              <label className="block text-sm font-body font-medium text-gray-700 mb-2">
                <ImageIcon size={18} className="inline mr-2" />
                {language === 'pt' ? 'Fotos do Local/Serviço (opcional)' : 'Photos of Location/Service (optional)'}
              </label>
              <div className="space-y-3">
                <p className="text-xs text-gray-500 font-body">
                  {language === 'pt' 
                    ? 'Envie fotos do local para ajudar na preparação (máx. 5 fotos, 5MB cada)' 
                    : 'Upload photos of the location to help with preparation (max. 5 photos, 5MB each)'}
                </p>
                
                {/* Upload Button */}
                <div>
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    multiple
                    onChange={handleFileUpload}
                    className="hidden"
                    data-testid="photo-file-input"
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingPhoto || formData.photos.length >= 5}
                    className="w-full h-24 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary hover:bg-primary/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex flex-col items-center justify-center gap-2"
                    data-testid="upload-photo-button"
                  >
                    {uploadingPhoto ? (
                      <>
                        <Loader2 size={24} className="text-primary animate-spin" />
                        <span className="text-sm text-gray-600">{language === 'pt' ? 'Enviando...' : 'Uploading...'}</span>
                      </>
                    ) : (
                      <>
                        <Upload size={24} className="text-gray-400" />
                        <span className="text-sm text-gray-600">
                          {language === 'pt' ? 'Clique para selecionar fotos' : 'Click to select photos'}
                        </span>
                        <span className="text-xs text-gray-400">
                          {language === 'pt' ? `${5 - formData.photos.length} fotos restantes` : `${5 - formData.photos.length} photos remaining`}
                        </span>
                      </>
                    )}
                  </button>
                </div>
                
                {/* Photo Preview Grid */}
                {formData.photos.length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {formData.photos.map((photo, index) => (
                      <div key={index} className="relative group" data-testid={`photo-preview-${index}`}>
                        <img
                          src={photo}
                          alt={`Preview ${index + 1}`}
                          className="w-full h-28 object-cover rounded-lg border-2 border-gray-200"
                          onError={(e) => e.target.src = 'https://via.placeholder.com/100x80?text=Erro'}
                        />
                        <button
                          type="button"
                          onClick={() => removePhoto(index)}
                          className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-md"
                          data-testid={`remove-photo-${index}`}
                        >
                          <X size={14} />
                        </button>
                        <div className="absolute bottom-1 left-1 bg-black/50 text-white text-xs px-1.5 py-0.5 rounded">
                          {index + 1}/{formData.photos.length}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div>
              <label htmlFor="notes" className="block text-sm font-body font-medium text-gray-700 mb-2">
                <MessageSquare size={18} className="inline mr-2" />
                {language === 'pt' ? 'Notas Adicionais' : 'Additional Notes'}
              </label>
              <textarea
                id="notes"
                name="notes"
                data-testid="booking-input-notes"
                value={formData.notes}
                onChange={handleChange}
                rows="4"
                className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent font-body"
                placeholder={language === 'pt' ? 'Detalhes adicionais sobre o serviço...' : 'Additional details about the service...'}
              ></textarea>
            </div>

            {submitStatus.message && (
              <div
                data-testid="booking-status-message"
                className={`p-4 rounded-md ${
                  submitStatus.type === 'success'
                    ? 'bg-green-50 text-green-800 border border-green-200'
                    : 'bg-red-50 text-red-800 border border-red-200'
                }`}
              >
                {submitStatus.message}
              </div>
            )}

            <button
              type="submit"
              data-testid="booking-submit-button"
              disabled={isSubmitting}
              className="w-full h-12 bg-primary text-white font-body font-semibold rounded-md hover:bg-primary-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting 
                ? (language === 'pt' ? 'Enviando...' : 'Submitting...') 
                : (language === 'pt' ? 'Confirmar Agendamento' : 'Confirm Booking')}
            </button>
          </form>
        </div>
      </section>

      {showConfirmModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          data-testid="confirm-modal"
        >
          <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-2xl">
            <div className="text-center mb-6">
              <div className="flex items-center justify-center w-16 h-16 bg-yellow-100 rounded-full mx-auto mb-4">
                <span className="text-3xl">⚠️</span>
              </div>
              <h3 className="text-2xl font-heading font-bold text-gray-900 mb-2">
                {language === 'pt' ? 'Confirmar Agendamento?' : 'Confirm Booking?'}
              </h3>
            </div>

            <div className="space-y-4 mb-6">
              <div className="bg-blue-50 border-l-4 border-primary p-4">
                <p className="text-sm font-body text-gray-800 mb-3">
                  <strong>{language === 'pt' ? 'Esta opção é para quem:' : 'This option is for those who:'}</strong>
                </p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start">
                    <span className="text-primary mr-2">✓</span>
                    <span className="font-body text-gray-700">
                      {language === 'pt' 
                        ? 'Já decidiu fazer o serviço com urgência' 
                        : 'Already decided to do the service urgently'}
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary mr-2">✓</span>
                    <span className="font-body text-gray-700">
                      {language === 'pt' 
                        ? 'Quer marcar data específica rapidamente' 
                        : 'Want to schedule specific date quickly'}
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary mr-2">✓</span>
                    <span className="font-body text-gray-700">
                      {language === 'pt' 
                        ? 'Não precisa de orçamento detalhado primeiro' 
                        : 'Does not need detailed quote first'}
                    </span>
                  </li>
                </ul>
              </div>

              <div className="bg-yellow-50 border-l-4 border-accent p-4">
                <p className="text-sm font-body text-gray-800">
                  <strong>⚡ {language === 'pt' ? 'Não tem pressa?' : 'Not in a hurry?'}</strong>
                  <br />
                  {language === 'pt'
                    ? 'Se ainda não tem certeza ou quer saber o preço primeiro, recomendamos usar "Pedir Orçamento" em vez de agendar.'
                    : 'If you are not sure yet or want to know the price first, we recommend using "Request Quote" instead of booking.'}
                </p>
              </div>
            </div>

            <div className="space-y-3">
              <button
                onClick={confirmAndSubmit}
                disabled={isSubmitting}
                className="w-full h-12 bg-primary text-white font-body font-semibold rounded-md hover:bg-primary-light transition-colors disabled:opacity-50"
                data-testid="confirm-yes-button"
              >
                {language === 'pt' 
                  ? '✓ Sim, quero agendar com urgência' 
                  : '✓ Yes, I want to book urgently'}
              </button>
              
              <a
                href="/ie/quote"
                className="block w-full h-12 bg-accent text-white font-body font-semibold rounded-md hover:bg-accent/90 transition-colors text-center leading-[3rem]"
                data-testid="go-to-quote-button"
              >
                {language === 'pt' 
                  ? 'Prefiro pedir orçamento primeiro' 
                  : 'I prefer to request quote first'}
              </a>

              <button
                onClick={() => setShowConfirmModal(false)}
                className="w-full h-12 bg-gray-100 text-gray-700 font-body font-medium rounded-md hover:bg-gray-200 transition-colors"
                data-testid="cancel-button"
              >
                {language === 'pt' ? 'Cancelar' : 'Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingPage;
